import { THEME } from '@Theme';
import { StyleSheet } from 'aphrodite';
import { overviewFieldLabelTypography } from './overviewFieldStyles';

/**
 * Layout for the Account Overview meeting-note composer, implementing the "Add Meeting Notes"
 * design: a wide (1000px) edge-to-edge dialog with a white header, a two-pane body (note editor on
 * the left with a Save / Next Meeting / Add-action-item footer, meeting metadata rail on the right),
 * stacking to one column on narrow widths. All tokens come from `THEME`.
 */
export const meetingNoteComposerStyles = StyleSheet.create({
	// Override the DialogPopup's default 24px padding + 512 max-width so the header/panes run
	// edge to edge; the internal panes own their own padding and scrolling.
	popup: {
		width: 1000,
		maxWidth: '100%',
		maxHeight: '90vh',
		padding: 0,
		overflow: 'hidden',
	},
	header: {
		flex: 'none',
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
		padding: `${THEME.spacing.lg - 4}px ${THEME.spacing.lg}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	title: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.primary.main,
		margin: 0,
	},
	grid: {
		display: 'grid',
		gridTemplateColumns: '1fr 360px',
		flex: 1,
		minHeight: 0,
		[THEME.queries.md]: {
			gridTemplateColumns: '1fr',
		},
	},
	left: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		borderRight: `1px solid ${THEME.colors.border.light}`,
		[THEME.queries.md]: {
			borderRight: 'none',
			borderBottom: `1px solid ${THEME.colors.border.light}`,
		},
	},
	leftScroll: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.md,
		minHeight: 0,
		overflowY: 'auto',
		padding: `${THEME.spacing.lg - 2}px ${THEME.spacing.lg}px 0`,
	},
	leftFooter: {
		display: 'flex',
		flex: 'none',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
		borderTop: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.main,
	},
	footerRow: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.lg,
	},
	// A footer action styled as a link (Next Meeting / Add action item): neutral text, colored icon.
	footLink: {
		alignItems: 'center',
		color: THEME.colors.text.main,
		display: 'inline-flex',
		fontWeight: THEME.typography.fontWeights.semibold,
		gap: THEME.spacing.sm,
		height: 'auto',
		padding: 0,
		width: 'fit-content',
		':hover': {
			color: THEME.colors.primary.main,
		},
	},
	sidebar: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.lg,
		overflowY: 'auto',
		padding: `${THEME.spacing.lg - 2}px ${THEME.spacing.lg}px`,
		backgroundColor: THEME.colors.background.light,
	},
	notePill: {
		alignItems: 'center',
		alignSelf: 'flex-start',
		backgroundColor: THEME.colors.pill.blue.background,
		border: `1px solid ${THEME.colors.pill.blue.border}`,
		borderRadius: 999,
		color: THEME.colors.pill.blue.icon,
		display: 'inline-flex',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		padding: `${THEME.spacing.xs + 1}px ${THEME.spacing.md - 2}px`,
	},
	field: {
		display: 'flex',
		flexDirection: 'column',
	},
	label: {
		...overviewFieldLabelTypography,
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
		marginBottom: THEME.spacing.sm + 2,
	},
	// Force the metadata Select triggers to fill the rail width.
	fullControl: {
		width: '100%',
	},
	onboardingRow: {
		alignItems: 'center',
		color: THEME.colors.text.main,
		cursor: 'pointer',
		display: 'flex',
		fontSize: THEME.typography.fontSizes.sm,
		gap: THEME.spacing.sm + 3,
		marginTop: THEME.spacing.sm + 6,
	},
});
